// 5-card KPI strip for the BMD report dashboard: pastel backgrounds, big
// number, descriptive subtitle. "Questions" is split into "Attempted" and
// "Not Attempted" so the tutor can read engagement separately from test size.
//
// SCOPE SELECTOR. Every number here counted the base test only, so once a
// prerequisite test ran the student could answer another 150 questions without
// a single figure moving. The dropdown swaps the WHOLE SET between the base
// test and the base test plus its prerequisite test - the same two options,
// worded the same way, as the radar below it.
//
// It swaps whole objects rather than individual fields on purpose: mixing a
// base-scoped count with an overall-scoped one in the same strip would be
// invisible and wrong.

import React from "react"
import type { BmdReportKpi } from "../types/bmd-report.types"

// `kpi.topics` counts dt_diagnostics_session_topic rows - the units a tutor
// picks when building a test. The business calls those CLUSTERS, so that is
// what the label says; the field keeps the name the API gives it.

type CardStyle = {
  label: string
  bgColor: string
  valueColor: string
}

// Bootstrap doesn't ship purple as a theme color in this build, so the SBs
// card uses inline pastel values to stay consistent with the others.
const CARD_STYLES: Record<string, CardStyle> = {
  attempted:    { label: "Attempted",     bgColor: "#fde2e1", valueColor: "#b42318" },
  notAttempted: { label: "Not Attempted", bgColor: "#fff4d6", valueColor: "#92400e" },
  correct:      { label: "Correct",       bgColor: "#d1fae5", valueColor: "#047857" },
  incorrect:    { label: "Incorrect",     bgColor: "#dbeafe", valueColor: "#1d4ed8" },
  sbs:          { label: "Skill Builders Queued", bgColor: "#ede9fe", valueColor: "#6d28d9" },
}

type CardProps = {
  styleKey: keyof typeof CARD_STYLES
  value: number | string
  subtitle: React.ReactNode
}

const KpiCard: React.FC<CardProps> = ({ styleKey, value, subtitle }) => {
  const s = CARD_STYLES[styleKey]
  return (
    <div
      className="rounded p-3 flex-fill"
      style={{ minWidth: "180px", backgroundColor: s.bgColor }}
    >
      <div className="text-uppercase fw-bold font-size-12 text-muted">
        {s.label}
      </div>
      <div
        className="fw-bold"
        style={{ fontSize: "32px", color: s.valueColor }}
      >
        {value}
      </div>
      <div className="font-size-13 text-muted">{subtitle}</div>
    </div>
  )
}

export type BmdReportKpiCardsProps = {
  /** Base test only. */
  kpi: BmdReportKpi | null
  /**
   * Base test plus the prerequisite test. Optional so an older response, or a
   * caller that has not been updated, simply loses the selector rather than
   * rendering an empty strip.
   */
  kpiOverall?: BmdReportKpi | null
}

type KpiScope = "base" | "overall"

/**
 * Which scope the strip opens on.
 *
 * Overall, on the PM's instruction: once a prerequisite test has been sat, the
 * base-test numbers are no longer the whole story, and a tutor who never opens
 * the dropdown was reading a partial picture as though it were the total. The
 * base test is still one click away.
 *
 * `kpi_overall` can be absent - an older backend omits it - in which case the
 * selector is not drawn and this falls back to the base numbers.
 */
const DEFAULT_KPI_SCOPE: KpiScope = "overall"

export const BmdReportKpiCards: React.FC<BmdReportKpiCardsProps> = ({
  kpi,
  kpiOverall,
}) => {
  const [scope, setScope] = React.useState<KpiScope>(DEFAULT_KPI_SCOPE)

  // Hooks run before the early return, so the selector cannot change the hook
  // order between renders.
  const canSwitch = !!kpiOverall
  const active = scope === "overall" && kpiOverall ? kpiOverall : kpi

  if (!active) {
    return (
      <div className="text-muted font-size-13">No diagnostic data yet.</div>
    )
  }

  // Mirrors the Correct card. accuracy_pct is correct over answered, so the
  // complement is the incorrect share of the same base. Taken as 100 minus
  // accuracy rather than rounded separately, so the two cards always add up to
  // 100 instead of landing on 34 and 67.
  //
  // The chain and FM counts are still on the response and unused here.
  const answered = active.correct + active.incorrect_total
  const incorrectSubtitle =
    answered > 0 ? `${100 - active.accuracy_pct}% of answers` : "no answers yet"

  const totalLabel = `of ${active.questions} total`

  return (
    <div className="mb-4">
      {canSwitch ? (
        <div className="d-flex align-items-center gap-2 mb-2">
          <label
            htmlFor="bmd-kpi-scope"
            className="text-muted font-size-12 mb-0"
          >
            Showing:
          </label>
          <select
            id="bmd-kpi-scope"
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={scope}
            onChange={(e) => setScope(e.target.value as KpiScope)}
          >
            <option value="base">Base test</option>
            <option value="overall">Overall (with prerequisite test)</option>
          </select>
        </div>
      ) : null}

      <div className="d-flex flex-wrap gap-3">
      <KpiCard
        styleKey="attempted"
        value={active.questions_attempted}
        subtitle={`${totalLabel} · across ${active.topics} cluster${
          active.topics === 1 ? "" : "s"
        }`}
      />
      <KpiCard
        styleKey="notAttempted"
        value={active.questions_not_attempted}
        subtitle={
          active.questions_not_attempted === 0
            ? "all questions answered"
            : `unanswered or skipped`
        }
      />
      <KpiCard
        styleKey="correct"
        value={active.correct}
        subtitle={`${active.accuracy_pct}% accuracy`}
      />
      <KpiCard
        styleKey="incorrect"
        value={active.incorrect_total}
        subtitle={incorrectSubtitle}
      />
      <KpiCard
        styleKey="sbs"
        value={active.sb_queued}
        subtitle={`${active.sb_completed} completed`}
      />
      </div>
    </div>
  )
}

export default BmdReportKpiCards
